import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, CreditCard, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function PublicDocument() {
  const { pubId } = useParams();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payAmount, setPayAmount] = useState("");

  useEffect(() => { api.getPublicDocument(pubId).then((d) => { setDoc(d); const dep = d.deposit_pct > 0 && (d.amount_paid || 0) <= 0.01; setPayAmount(String(dep ? d.deposit_amount : (d.balance ?? d.total ?? ""))); }).catch(() => setErr(true)); }, [pubId]);

  const pay = async (amount) => {
    setPaying(true);
    try {
      const res = await api.payPublicDocument(pubId, window.location.origin, amount);
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment");
      setPaying(false);
    }
  };

  if (err) return <div className="min-h-screen clean-bg flex items-center justify-center text-zinc-400">This document could not be found.</div>;
  if (!doc) return <div className="min-h-screen clean-bg flex items-center justify-center text-zinc-500">Loading…</div>;

  const sym = doc.currency_symbol || "$";
  const money = (v) => `${sym}${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const biz = doc.business || {};
  const c = doc.client || {};
  const isInvoice = doc.type === "invoice";
  const balance = doc.balance ?? doc.total ?? 0;
  const amountPaid = doc.amount_paid || 0;
  const fullyPaid = doc.payment_state === "paid" || (isInvoice && balance <= 0.01);
  const partial = doc.payment_state === "partial";
  const accent = biz.accent || "#2563eb";
  const depositRequired = isInvoice && (doc.deposit_pct || 0) > 0 && amountPaid <= 0.01;
  const depositAmount = doc.deposit_amount || 0;
  const belowDeposit = depositRequired && Number(payAmount) < depositAmount - 0.01;

  return (
    <div className="min-h-screen clean-bg text-white py-10 px-4" data-testid="public-document">
      <div className="max-w-3xl mx-auto space-y-6 fade-in">
        <div className="glass-panel rounded-2xl p-8">
          {/* Header */}
          <div className="flex justify-between items-start gap-4 flex-wrap">
            <div className="flex items-start gap-4">
              {biz.has_logo && <img src={api.genLogoUrl(biz.profile_id)} alt="logo" className="w-16 h-16 object-contain rounded-lg bg-white/5 p-1" />}
              <div>
                <div className="text-xl font-bold text-white">{biz.business_name || "Business"}</div>
                <div className="text-xs text-zinc-400 mt-1 space-y-0.5">
                  {biz.address && <div>{biz.address}</div>}
                  {biz.business_email && <div>{biz.business_email}</div>}
                  {biz.phone && <div>{biz.phone}</div>}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold" style={{ color: accent }}>{isInvoice ? "INVOICE" : "QUOTE"}</div>
              <div className="text-sm text-zinc-300 font-bold">#{doc.number}</div>
              {doc.issue_date && <div className="text-xs text-zinc-500 mt-1">Issued: {doc.issue_date}</div>}
              {doc.due_date && <div className="text-xs text-zinc-500">{isInvoice ? "Due" : "Valid until"}: {doc.due_date}</div>}
              {fullyPaid && <div className="mt-2 inline-flex items-center gap-1 text-green-400 text-xs font-bold uppercase"><CheckCircle2 size={13} /> Paid</div>}
              {partial && <div className="mt-2 inline-flex items-center gap-1 text-amber-400 text-xs font-bold uppercase">Partially Paid</div>}
            </div>
          </div>

          <div className="h-px my-6" style={{ background: accent, opacity: 0.4 }} />

          {c?.name && (
            <div className="mb-6">
              <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1">Bill To</div>
              <div className="text-white font-bold">{c.name}</div>
              <div className="text-xs text-zinc-400">{[c.company, c.email, c.phone].filter(Boolean).join(" · ")}</div>
            </div>
          )}

          {/* Items */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] text-zinc-500 uppercase tracking-wider border-b border-white/10"><th className="text-left py-2">Description</th><th className="text-right py-2">Qty</th><th className="text-right py-2">Rate</th><th className="text-right py-2">Amount</th></tr></thead>
              <tbody>
                {(doc.line_items || []).map((li, i) => (
                  <tr key={li.id || i} className="border-b border-white/5">
                    <td className="py-2.5 text-zinc-200">{li.description}</td>
                    <td className="py-2.5 text-right text-zinc-400">{li.qty}</td>
                    <td className="py-2.5 text-right text-zinc-400">{money(li.rate)}</td>
                    <td className="py-2.5 text-right text-white font-medium">{money((li.qty || 0) * (li.rate || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end mt-4">
            <div className="w-full max-w-xs space-y-1.5">
              <div className="flex justify-between text-sm text-zinc-400"><span>Subtotal</span><span>{money(doc.subtotal)}</span></div>
              {doc.discount_amount > 0 && <div className="flex justify-between text-sm text-teal-400"><span>Discount</span><span>-{money(doc.discount_amount)}</span></div>}
              {doc.tax_amount > 0 && <div className="flex justify-between text-sm text-zinc-400"><span>Tax</span><span>{money(doc.tax_amount)}</span></div>}
              <div className="flex justify-between text-lg font-bold text-white pt-2 border-t border-white/10"><span>Total</span><span data-testid="public-doc-total">{money(doc.total)}</span></div>
              {isInvoice && amountPaid > 0 && (
                <>
                  <div className="flex justify-between text-sm text-green-400 pt-1"><span>Paid</span><span>-{money(amountPaid)}</span></div>
                  <div className="flex justify-between text-base font-bold text-white pt-1 border-t border-white/10"><span>Balance Due</span><span data-testid="public-doc-balance">{money(balance)}</span></div>
                </>
              )}
            </div>
          </div>

          {doc.notes && <div className="mt-6 text-xs text-zinc-400"><div className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1">Notes</div>{doc.notes}</div>}
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <a href={api.genDocPdfUrl(pubId)} target="_blank" rel="noopener noreferrer" className="w-full py-3.5 rounded-xl bg-white/5 text-white font-bold hover:bg-white/10 transition flex items-center justify-center" data-testid="public-download-pdf"><Download size={17} className="mr-2" /> Download PDF</a>

          {isInvoice && !fullyPaid && (
            <div className="glass-panel rounded-2xl p-5 space-y-4" data-testid="public-pay-panel">
              <div className="text-sm font-bold text-white">Make a Payment</div>
              {depositRequired && (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-300" data-testid="deposit-required-note">
                  A minimum deposit of <b>{doc.deposit_pct}%</b> ({money(depositAmount)}) is required to start this project.
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setPayAmount(String(balance))} className="px-3 py-1.5 rounded-lg bg-white/5 text-zinc-200 text-xs font-bold hover:bg-white/10" data-testid="pay-full-preset">Full balance ({money(balance)})</button>
                {depositRequired ? (
                  <button onClick={() => setPayAmount(String(depositAmount))} className="px-3 py-1.5 rounded-lg bg-amber-500/10 text-amber-300 border border-amber-500/20 text-xs font-bold hover:bg-amber-500/20" data-testid="pay-deposit-preset">Required deposit ({money(depositAmount)})</button>
                ) : (
                  <button onClick={() => setPayAmount((balance / 2).toFixed(2))} className="px-3 py-1.5 rounded-lg bg-white/5 text-zinc-200 text-xs font-bold hover:bg-white/10" data-testid="pay-deposit-preset">50% deposit ({money(balance / 2)})</button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-zinc-400 text-lg">{sym}</span>
                <input type="number" min={depositRequired ? depositAmount : 1} max={balance} step="0.01" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} className="flex-1 px-4 py-3 rounded-xl dark-input text-sm" data-testid="pay-amount-input" />
              </div>
              {belowDeposit && <p className="text-xs text-amber-400">Enter at least {money(depositAmount)} to meet the required deposit.</p>}
              <button onClick={() => pay(Number(payAmount))} disabled={paying || !(Number(payAmount) > 0) || belowDeposit} className="w-full py-3.5 rounded-xl text-white font-bold transition flex items-center justify-center disabled:opacity-60" style={{ backgroundColor: accent }} data-testid="public-pay-btn">
                <CreditCard size={17} className="mr-2" /> {paying ? "Redirecting…" : `Pay ${money(Number(payAmount) || 0)}`}
              </button>
              <p className="text-center text-[11px] text-zinc-500">Pay the full balance or a partial deposit — we'll track the rest automatically.</p>
            </div>
          )}
          {isInvoice && fullyPaid && (
            <div className="w-full py-3.5 rounded-xl bg-green-500/10 text-green-400 border border-green-500/20 font-bold flex items-center justify-center"><CheckCircle2 size={17} className="mr-2" /> Paid in full</div>
          )}
        </div>
        <p className="text-center text-xs text-zinc-600">Secured by Stripe · Powered by DONDRÁE</p>
      </div>
    </div>
  );
}
